// Expiry values arrive as epoch seconds (login response, getSession.asp);
// cached copies may already be in milliseconds, older caches hold ISO date
// strings. Normalize all of them to epoch milliseconds, which are UTC and
// compare directly against Date.now() whatever the user's timezone.
export const toMillis = (expires) => {
  const value = typeof expires === 'string' && Number.isNaN(Number(expires)) ? Date.parse(expires) : Number(expires);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value < 1e12 ? value * 1000 : value;
};

// True once the expiry has passed. An unreadable expiry is not treated as
// expired here; callers that require one check for it themselves.
export const isExpired = (expires, nowMs = Date.now()) => {
  const expiresAtMs = toMillis(expires);
  return expiresAtMs !== null && nowMs >= expiresAtMs;
};

// The exp claim of a JWT access token in epoch milliseconds, or null when the
// token is not a readable JWT. Only decodes; the signature is the server's
// concern.
export const getJwtExpiryMs = (token) => {
  try {
    // JWT payloads use base64url (- and _ in place of + and /); atob only
    // reads standard base64, so swap the characters before decoding.
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return toMillis(JSON.parse(atob(payload)).exp);
  } catch {
    return null;
  }
};

// Access token expiry for either auth flow: the chat link flow stores
// accessTokenExpiresAt, the website flow stores expires. A JWT access token
// also carries its own exp; the earliest known value wins, so a missing or
// overstated stored expiry cannot push the timeline past the real one. Null
// when nothing is known.
export const getAccessTokenExpiryMs = (authData) => {
  const known = [
    toMillis(authData?.accessTokenExpiresAt),
    toMillis(authData?.expires),
    getJwtExpiryMs(authData?.accessToken),
  ].filter((expiresAtMs) => expiresAtMs !== null);
  return known.length ? Math.min(...known) : null;
};

// True when a token expiring at expiresAtMs reaches its decision point no
// sooner than MIN_RENEWAL_INTERVAL_MS from now. A renewal that fails this
// did not extend the session and must not be applied.
export const extendsSession = (expiresAtMs, nowMs, { DECISION_LEAD_MS, MIN_RENEWAL_INTERVAL_MS }) =>
  expiresAtMs !== null && expiresAtMs - DECISION_LEAD_MS - nowMs >= MIN_RENEWAL_INTERVAL_MS;

// When the renew-or-warn decision is due and when the forced logout happens
// for a token expiring at expiresAtMs. A token that already has less than
// the logout buffer left (e.g. handed over late) still gets its decision,
// counting down to the real expiry, instead of logging out on arrival.
export const getSessionTimeline = (expiresAtMs, nowMs, { DECISION_LEAD_MS, LOGOUT_BUFFER_MS }) => {
  const bufferedLogoutAt = expiresAtMs - LOGOUT_BUFFER_MS;
  return {
    decideAt: expiresAtMs - DECISION_LEAD_MS,
    logoutAt: nowMs < bufferedLogoutAt ? bufferedLogoutAt : expiresAtMs,
  };
};
